/**
 * Daily evening reminders in the iOS app, scheduled on the phone itself.
 * Nothing is sent from our servers and no journal text is ever read —
 * the reminders are fixed, gentle messages the member switches on.
 */
import { isNativeApp } from "./native-app";

const SELF_CARE_ID = 7101;
const JOURNAL_ID = 7102;

export async function remindersAvailable(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.isPluginAvailable("LocalNotifications");
  } catch {
    return false;
  }
}

export async function remindersOn(): Promise<boolean> {
  if (!(await remindersAvailable())) return false;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== "granted") return false;
    const { notifications } = await LocalNotifications.getPending();
    return notifications.some((n) => n.id === SELF_CARE_ID || n.id === JOURNAL_ID);
  } catch {
    return false;
  }
}

export async function enableReminders(): Promise<{ ok: boolean; reason?: "denied" | "unsupported" | "error" }> {
  if (!(await remindersAvailable())) return { ok: false, reason: "unsupported" };
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== "granted") return { ok: false, reason: "denied" };
    await LocalNotifications.cancel({ notifications: [{ id: SELF_CARE_ID }, { id: JOURNAL_ID }] });
    await LocalNotifications.schedule({
      notifications: [
        {
          id: SELF_CARE_ID,
          title: "A small act of care",
          body: "Your self-care calendar has a gentle idea for today.",
          schedule: { on: { hour: 19, minute: 0 }, allowWhileIdle: true },
          extra: { url: "/self-care-calendar" },
        },
        {
          id: JOURNAL_ID,
          title: "A moment for you",
          body: "If it feels right, write a few lines in your journal. There's no right way to do it.",
          schedule: { on: { hour: 19, minute: 30 }, allowWhileIdle: true },
          extra: { url: "/journal" },
        },
      ],
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

export async function disableReminders(): Promise<void> {
  if (!(await remindersAvailable())) return;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.cancel({ notifications: [{ id: SELF_CARE_ID }, { id: JOURNAL_ID }] });
  } catch {
    /* ignore */
  }
}

/** Opens the right page when a member taps a reminder. */
export async function installReminderTapHandler(navigate: (path: string) => void): Promise<void> {
  if (!(await remindersAvailable())) return;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
      const url = (action.notification.extra as { url?: unknown } | undefined)?.url;
      if (typeof url === "string" && url.startsWith("/")) navigate(url);
    });
  } catch {
    /* ignore */
  }
}
